import { type SWRConfiguration } from 'swr';
import {
  DisasterRecoveryProposalAction,
  type DisasterRecoveryRecordStatus,
} from '@mls-wallet/shared-types';
import type { Signature } from '@/types/signature';
import type { ActiveDRSupportingProposal } from './use-disaster-recovery-settings';
import { type DisasterRecoveryProposal } from '../../../types/disaster-recovery-proposal';
import {
  useDisasterRecoveryProposalList,
  type ServerDisasterRecoveryProposalsResponse,
} from './use-disaster-recovery-proposal-list';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

export type ActiveDisasterRecoveryProposalResponse = {
  proposal: DisasterRecoveryProposal;
  isExecuting: boolean;
  approvals: Signature[];
  rejections: Signature[];
  linkedDisasterRecoveryStatus: DisasterRecoveryRecordStatus | null;
  requiredApprovals: number;
  totalVoters: number;
} | null;

type ActiveDRSupportingProposalAction =
  | typeof DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY
  | typeof DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY;

type ActiveDRProposalAction =
  | typeof DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY
  | ActiveDRSupportingProposalAction;

type ActiveDRProposalResponse =
  | ActiveDisasterRecoveryProposalResponse
  | ActiveDRSupportingProposal;

type ActiveDRProposalSWRConfig = Omit<
  SWRConfiguration<ServerDisasterRecoveryProposalsResponse>,
  'fallbackData'
>;

function selectProposalByAction(
  action: ActiveDRProposalAction,
  response: ServerDisasterRecoveryProposalsResponse | null
): ActiveDRProposalResponse {
  if (!response) {
    return null;
  }
  if (
    action === DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY
  ) {
    return response.drInitiationProposal;
  }
  if (action === DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY) {
    return response.drCancellationProposal;
  }
  return response.drFinalizationProposal;
}

/**
 * Hook to fetch the active disaster recovery proposal for an organization.
 *
 * @param action - The action to fetch the proposal for (INITIATE, CANCEL, FINALIZE)
 * @param swrOptions - Optional SWR configuration overrides
 * @returns The active proposal with approval/rejection signatures, or null if none exists
 */
export function useActiveDRProposal(
  action: ActiveDRProposalAction,
  swrOptions?: ActiveDRProposalSWRConfig
) {
  const { data, error, isLoading, refresh } = useDisasterRecoveryProposalList({
    refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
    ...(swrOptions ?? {}),
  });

  return {
    activeProposal: selectProposalByAction(action, data),
    error,
    isLoading,
    refresh,
  };
}
